import { useState, useRef } from "react";
import { Lightbulb, Send, ChevronDown, ChevronUp, ExternalLink, User, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { FormattedText, FormattingToolbar } from "@/components/ui/formatted-text";

const API = `${getApiUrl()}/api`;

// Fact categories with labels and icons
const FACT_CATEGORIES = [
  { value: "developer_insight", label: "Developer Insight", icon: "🎮" },
  { value: "easter_egg", label: "Easter Egg", icon: "🥚" },
  { value: "historical_context", label: "Historical Context", icon: "📜" },
  { value: "pop_culture_reference", label: "Pop Culture Reference", icon: "🎬" },
  { value: "speedrun_tip", label: "Speedrun Tip", icon: "⏱️" },
  { value: "lore", label: "Lore & Story", icon: "📖" },
  { value: "fun_fact", label: "Fun Fact", icon: "💡" },
  { value: "behind_the_scenes", label: "Behind the Scenes", icon: "🎥" },
  { value: "other", label: "Other", icon: "📝" },
];

// Get category info by value
export const getCategoryInfo = (categoryValue) => {
  return FACT_CATEGORIES.find(c => c.value === categoryValue) || FACT_CATEGORIES[6]; // default to fun_fact
};

/**
 * Fact Icon Button - Shows indicator for facts on achievement
 * Gray when no facts, colored when facts exist
 */
export function FactIconButton({ 
  factCount = 0, 
  onClick,
  disabled = false,
  size = "default",
  title
}) {
  const hasFacts = factCount > 0;
  const sizeClasses = {
    sm: "h-7 w-7",
    default: "h-8 w-8",
    lg: "h-9 w-9"
  };
  const iconSizes = {
    sm: "w-3.5 h-3.5",
    default: "w-4 h-4",
    lg: "w-5 h-5"
  };
  
  const defaultTitle = hasFacts 
    ? `${factCount} fact${factCount !== 1 ? 's' : ''}` 
    : "No facts - click to submit one";
  
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      disabled={disabled}
      className={`${sizeClasses[size]} ${
        disabled
          ? "text-white/10 cursor-not-allowed"
          : hasFacts 
            ? "text-amber-400 hover:text-amber-300 hover:bg-amber-400/10" 
            : "text-white/20 hover:text-white/40 hover:bg-white/5"
      }`}
      title={title || defaultTitle}
      data-testid="fact-icon-btn"
    >
      <Lightbulb className={iconSizes[size]} />
      {factCount > 1 && !disabled && (
        <span className="absolute -top-1 -right-1 text-[10px] bg-amber-500 text-black rounded-full w-4 h-4 flex items-center justify-center font-bold">
          {factCount > 9 ? "9+" : factCount}
        </span>
      )}
    </Button>
  );
}

/**
 * Facts Display Panel - Expandable section showing facts for an achievement
 */
export function FactsPanel({ 
  facts = [], 
  isOpen, 
  onToggle,
  onSubmitClick,
  achievementName 
}) {
  if (facts.length === 0 && !isOpen) return null;
  
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleContent className="mt-3 pt-3 border-t border-white/10">
        {facts.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h5 className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                <Lightbulb className="w-3 h-3" />
                Facts & Trivia
              </h5>
              <Button
                variant="ghost"
                size="sm"
                onClick={onSubmitClick}
                className="text-white/40 hover:text-white text-xs h-6 px-2"
              >
                <Send className="w-3 h-3 mr-1" />
                Submit Fact
              </Button>
            </div>
            <div className="space-y-2">
              {facts.map((fact) => (
                <FactCard key={fact.id} fact={fact} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <Lightbulb className="w-8 h-8 mx-auto text-white/20 mb-2" />
            <p className="text-white/40 text-sm mb-3">No facts yet for this achievement</p>
            <Button
              variant="outline"
              size="sm"
              onClick={onSubmitClick}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            >
              <Send className="w-3 h-3 mr-1.5" />
              Submit a Fact
            </Button>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Single Fact Card with Like functionality
 */
export function FactCard({ fact, onLikeUpdate }) {
  const category = getCategoryInfo(fact.category);
  const [liked, setLiked] = useState(fact.isLikedByUser || false);
  const [likesCount, setLikesCount] = useState(fact.likesCount || 0);
  const [liking, setLiking] = useState(false);
  
  const handleLike = async (e) => {
    e.stopPropagation();
    if (liking) return;
    
    setLiking(true);
    try {
      const response = await axios.post(`${API}/achievement-facts/facts/${fact.id}/like`);
      setLiked(response.data.liked);
      setLikesCount(response.data.likesCount);
      if (onLikeUpdate) {
        onLikeUpdate(fact.id, response.data.liked, response.data.likesCount);
      }
    } catch (error) {
      console.error("Failed to like fact:", error);
      toast.error("Failed to save");
    } finally {
      setLiking(false);
    }
  };
  
  return (
    <div className="bg-black/30 rounded-lg p-3 border border-amber-500/10">
      <div className="flex items-start gap-2">
        <span className="text-base" title={category.label}>{category.icon}</span>
        <div className="flex-1 min-w-0">
          <FormattedText text={fact.factText} className="text-white/90 text-sm leading-relaxed" />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3 text-xs text-white/40">
              <span className="text-amber-400/70">{category.label}</span>
              {fact.sourceUrl && (
                <a 
                  href={fact.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-white/60"
                >
                  <ExternalLink className="w-3 h-3" />
                  Source
                </a>
              )}
              {fact.showSubmitter && fact.submittedByUsername && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {fact.submittedByUsername}
                </span>
              )}
            </div>
            <button
              onClick={handleLike}
              disabled={liking}
              className={`flex items-center gap-1 text-xs transition-colors ${
                liked 
                  ? "text-pink-400 hover:text-pink-300" 
                  : "text-white/30 hover:text-pink-400"
              }`}
              title={liked ? "Unlike" : "Like & save"}
              data-testid={`like-fact-${fact.id}`}
            >
              <Heart className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} />
              {likesCount > 0 && <span>{likesCount}</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Submit Fact Dialog - Allows users to submit facts for review
 */
export function SubmitFactDialog({
  open,
  onOpenChange,
  achievementId,
  achievementName,
  gameName,
  onSubmit,
  isSubmitting
}) {
  const [factText, setFactText] = useState("");
  const [category, setCategory] = useState("fun_fact");
  const [sourceUrl, setSourceUrl] = useState("");
  const [showSubmitter, setShowSubmitter] = useState(false);
  const submitTextareaRef = useRef(null);
  
  const handleSubmit = async () => {
    if (!factText.trim()) return;
    
    await onSubmit({
      factText: factText.trim(),
      category,
      sourceUrl: sourceUrl.trim() || null,
      showSubmitter
    });
    
    // Reset form on success
    setFactText("");
    setCategory("fun_fact");
    setSourceUrl("");
    setShowSubmitter(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-400" />
            Submit a Fact
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Share trivia about &quot;{achievementName}&quot; from {gameName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Your Fact or Trivia *</Label>
            <FormattingToolbar
              textareaRef={submitTextareaRef}
              value={factText}
              onChange={setFactText}
            />
            <Textarea
              ref={submitTextareaRef}
              placeholder="Share something interesting about this achievement..."
              value={factText}
              onChange={(e) => setFactText(e.target.value)}
              className="bg-black/50 border-white/10 min-h-[100px] resize-none"
              maxLength={500}
            />
            <p className="text-white/30 text-xs text-right">{factText.length}/500</p>
          </div>
          
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10">
                {FACT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <span className="flex items-center gap-2">
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Source URL (optional)</Label>
            <Input
              placeholder="https://..."
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              className="bg-black/50 border-white/10"
            />
            <p className="text-white/30 text-xs">Link to where you found this information</p>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-black/30 rounded-lg">
            <Checkbox
              id="show-credit"
              checked={showSubmitter}
              onCheckedChange={setShowSubmitter}
              className="border-white/30"
            />
            <div>
              <Label htmlFor="show-credit" className="cursor-pointer">
                Credit me for this submission
              </Label>
              <p className="text-white/30 text-xs">Your username will be shown if approved</p>
            </div>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!factText.trim() || isSubmitting}
              className="flex-1 bg-amber-600 hover:bg-amber-500"
            >
              {isSubmitting ? "Submitting..." : "Submit for Review"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { FACT_CATEGORIES };
